

import React, { useState } from "react";
import { Form, Button, Row, Col, Accordion, Table, Badge, Spinner, Alert } from "react-bootstrap";
import {
  Star,
  Mail,
  Filter,
  Plus,
  MoreHorizontal,
  Globe,
  Building2,
  Phone,
  User,
  Users,
  AlertCircle,
} from "lucide-react";
import ContactPopupView from "../../../contacts/contactpopup/ContactPopupView";

const OrganizationSummaryTab = ({
  organization,
  description,
  setDescription,
  isEditingDescription,
  setIsEditingDescription,
  contacts = [],
  users = [],
}) => {
  const [selectedContactForPopup, setSelectedContactForPopup] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [defaultActiveTab, setDefaultActiveTab] = useState("summary");

  // Function to get contact persons by comma-separated IDs or enriched array
  const getContactPersonsByIds = (contactPersonIds) => {
    if (!contactPersonIds) return [];
    
    // If it's already an enriched array from backend
    if (Array.isArray(contactPersonIds)) return contactPersonIds;

    // If it's a string (CSV), find matching contacts from the contacts prop
    if (typeof contactPersonIds === "string" && contacts.length) {
      const contactIds = contactPersonIds.split(",").map((id) => id.trim());
      return contactIds
        .map((id) => contacts.find((contact) => contact.id === id))
        .filter((contact) => contact !== undefined);
    }

    return [];
  };

  // Get all contacts for this organization
  const getOrganizationContacts = () => {
    // Start with contacts from organization.contactPersonIds if they are objects
    const directContacts = Array.isArray(organization?.contactPersonIds) 
      ? organization.contactPersonIds.filter(cp => typeof cp === 'object' && cp.id)
      : [];

    // Also get contacts from the contacts prop that belong to this organization
    const propContacts = contacts.filter(
      (contact) => contact.organizationId === organization.id || contact.organizationId === "Not Available",
    );

    // Merge and remove duplicates by ID
    const merged = [...directContacts];
    propContacts.forEach(pc => {
      if (!merged.some(m => m.id === pc.id)) {
        merged.push(pc);
      }
    });

    return merged;
  };

  // Get all HR contacts for this organization
  const getOrganizationHRContacts = () => {
    return getOrganizationContacts();
  };

  // Get assigned HR person details by assignedToIds (handles string or array)
  const getAssignedHRPerson = (assignedToIds) => {
    if (!assignedToIds) return null;

    // If it's already an enriched array from backend
    if (Array.isArray(assignedToIds)) {
      return assignedToIds.length > 0 ? assignedToIds[0] : null;
    }

    // If it's a string ID, find in users prop
    if (typeof assignedToIds === "string" && users.length) {
      const user = users.find((user) => user.id === assignedToIds);
      return user || null;
    }

    return null;
  };

  const handleViewContact = (contactId) => {
    const allContacts = getOrganizationContacts();
    const contact = allContacts.find((c) => c.id === contactId)
    if (contact) {
      setSelectedContactForPopup(contact)
      setDefaultActiveTab("summary")
      setShowContactPopup(true)
    }
  };

  const handleUpdateContactCallback = (updatedContact) => {
    setSelectedContactForPopup(updatedContact);
  };

  const handleCloseContactPopup = () => {
    setShowContactPopup(false);
    setSelectedContactForPopup(null);
  };

  // Get all contact persons for this organization
  const organizationContacts = getContactPersonsByIds(
    organization.contactPersonIds
  );
  const primaryContact =
    organizationContacts.length > 0 ? organizationContacts[0] : null;

  // Get assigned user
  const assignedUser = getAssignedHRPerson(organization.assignedToIds);

  // Get HR contacts for right panel
  const organizationHRContacts = getOrganizationHRContacts();

  // Get contact person email and phone
  const contactPersonEmail = primaryContact?.email || organization.email || "N/A";
  const contactPersonPhone = primaryContact?.phone || organization.phone || "N/A";

  return (
    <div className="organization-summary-content">
      <Row className="h-100" style={{ margin: 0 }}>
        {/* Left Column - Organization Details */}
        <Col md={9} style={{ borderRight: "1px solid #e9ecef", paddingRight: "20px", maxHeight: "700px", overflowY: "auto" }}>
          
          {/* Header Row - Labels */}
          <Row className="org-header-row">
            <Col md={2}>
              <span className="field-label">Organization Name</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Contact Person</span>
            </Col>
            <Col md={3}>
              <span className="field-label">Email</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Phone</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Website</span>
            </Col>
            <Col md={2}></Col>
          </Row>

          {/* Data Row 1 */}
          <Row className="org-data-row">
            <Col md={2}>
              <span className="field-value">{organization.name || "N/A"}</span>
            </Col>
            <Col md={2}>
              {organizationContacts.length > 0 ? (
                <div>
                  <span className="field-value">{primaryContact.name}</span>
                  {organizationContacts.length > 1 && (
                    <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "2px" }}>
                      +{organizationContacts.length - 1} more
                    </div>
                  )}
                </div>
              ) : (
                <span className="field-value text-muted">Not Assigned</span>
              )}
            </Col>
            <Col md={3}>
              <span className="field-value">
                {contactPersonEmail && contactPersonEmail !== "N/A" ? (
                  <a href={`mailto:${contactPersonEmail}`} style={{ color: "#ea580c", textDecoration: "none" }}>
                    {contactPersonEmail}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </Col>
            <Col md={2}>
              <span className="field-value">
                {contactPersonPhone && contactPersonPhone !== "N/A" ? (
                  <a href={`tel:${contactPersonPhone}`} style={{ color: "#ea580c", textDecoration: "none" }}>
                    {contactPersonPhone}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </Col>
            <Col md={2}>
              <span className="field-value" style={{ color: "#0d6efd" }}>
                {organization.website ? (
                  <a
                    href={organization.website.startsWith("http") ? organization.website : `https://${organization.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0d6efd", textDecoration: "none" }}
                  >
                    {organization.website}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </Col>
            <Col md={2}></Col>
          </Row>

          {/* Additional Row - Labels */}
          <Row className="org-additional-row mt-3">
            <Col md={2}>
              <span className="field-label">Status</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Assigned To</span>
            </Col>
            { organization.source &&
              <Col md={2}>
              <span className="field-label">Source</span>
            </Col>}
            {
              organization.description &&
              <Col md={3}>
              <span className="field-label">Description</span>
            </Col>}
            <Col md={3}></Col>
          </Row>

          {/* Additional Row - Data */}
          <Row className="org-data-row">
            <Col md={2}>
              <span className="badge bg-secondary">
                {organization.status || "N/A"}
              </span>
            </Col>
            <Col md={2}>
              {assignedUser ? (
                <div className="assigned-user">
                  <div className="user-avatar">
                    {`${assignedUser.firstName?.substring(0, 1) || ""}${assignedUser.lastName?.substring(0, 1) || ""}`.toUpperCase()}
                  </div>
                  <span className="user-name">
                    {`${assignedUser.firstName} ${assignedUser.lastName}`.toLowerCase()}
                  </span>
                </div>
              ) : (
                <span className="field-value text-muted">Not assigned</span>
              )}
            </Col>
           {
              organization.source && 
            <Col md={2}>
              <span className="field-value">
                {organization.source || "N/A"}
              </span>
            </Col>}
            {
              organization.description &&
              <Col md={3}>
              <span className="field-value">
                {organization.description || "Not specified"}
              </span>
            </Col>}
            <Col md={3}></Col>
          </Row>
        </Col>

        {/* Right Column - HR Contacts */}
        <Col md={3} style={{ paddingLeft: "20px", display: "flex", flexDirection: "column" }}>
          <div className="hr-contacts-sidebar">
            <h6 className="mb-3" style={{ fontWeight: 600, fontSize: "16px" }}>
              <Users size={18} className="me-2" style={{ display: "inline" }} />
              HR Contacts
            </h6>

            {organizationHRContacts && organizationHRContacts.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <div className="mb-3">
                  <small className="text-muted">
                    Total Contacts{" "}
                    <span className="badge bg-primary">
                      {organizationHRContacts.length}
                    </span>
                  </small>
                </div>

                <div className="hr-contacts-list" style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto", flex: 1 }}>
                  {organizationHRContacts.map((contact, index) => {
                    const assignedHR = getAssignedHRPerson(contact.assignedToId);

                    return (
                      <div
                        key={contact.id || index}
                        className="hr-contact-card"
                        style={{
                          border: "1px solid #e9ecef",
                          borderRadius: "6px",
                          padding: "12px",
                          marginBottom: "10px",
                          backgroundColor: "#f8f9fa",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e9ecef";
                          e.currentTarget.style.boxShadow =
                            "0 2px 4px rgba(0,0,0,0.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                        onClick={() => handleViewContact(contact.id)}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className="hr-contact-title"
                              style={{
                                fontWeight: 600,
                                marginBottom: "4px",
                                fontSize: "0.9em",
                                color: "#0d6efd",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {contact.name || "N/A"}
                            </div>
                          </div>
                        </div>

                        {contact.email && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#999",
                              marginBottom: "4px",
                              wordBreak: "break-word",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Mail size={12} />
                            <a href={`mailto:${contact.email}`} style={{ color: "#999", textDecoration: "none" }}>
                              {contact.email}
                            </a>
                          </div>
                        )}

                        {contact.phone && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#999",
                              marginBottom: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Phone size={12} />
                            <a href={`tel:${contact.phone}`} style={{ color: "#999", textDecoration: "none" }}>
                              {contact.phone}
                            </a>
                          </div>
                        )}

                        {assignedHR && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              marginTop: "6px",
                              paddingTop: "6px",
                              borderTop: "1px solid #e9ecef",
                            }}
                          >
                            <strong>Assigned HR:</strong>{" "}
                            {`${assignedHR.firstName || ""} ${
                              assignedHR.lastName || ""
                            }`.trim()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-4" style={{ color: "#6c757d" }}>
                <Users size={32} className="mb-2" style={{ opacity: 0.5 }} />
                <p style={{ fontSize: "14px" }}>No HR Contacts</p>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {selectedContactForPopup && (
        <ContactPopupView
          show={showContactPopup}
          onHide={handleCloseContactPopup}
          contact={selectedContactForPopup}
          onUpdateContact={handleUpdateContactCallback}
          defaultActiveTab={defaultActiveTab}
        />
      )}

      <style>{`
        .organization-summary-content {
          padding: 20px 0;
        }

        .org-header-row {
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 10px;
          margin-bottom: 15px;
        }

        .org-data-row {
          margin-bottom: 20px;
        }

        .org-additional-row {
          border-top: 1px solid #e9ecef;
          padding-top: 20px;
          margin-bottom: 30px;
        }

        .field-label {
          font-size: 0.85rem;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .field-value {
          font-size: 0.9rem;
          color: #212529;
          margin-top: 5px;
          font-weight: 500;
        }

        .assigned-user {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 0.8rem;
        }

        .user-name {
          font-size: 0.9rem;
          color: #495057;
        }

        .badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .hr-contacts-sidebar {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: 700px;
        }

        .hr-contacts-list {
          scroll-behavior: smooth;
        }

        .hr-contacts-list::-webkit-scrollbar {
          width: 6px;
        }

        .hr-contacts-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .hr-contacts-list::-webkit-scrollbar-thumb {
          background: #dee2e6;
          border-radius: 3px;
        }

        .hr-contacts-list::-webkit-scrollbar-thumb:hover {
          background: #adb5bd;
        }

        .hr-contact-card {
          transition: all 0.2s ease;
        }

        .hr-contact-card:hover {
          transform: translateY(-2px);
        }

        .hr-contact-title {
          font-weight: 600;
          margin-bottom: 4px;
          font-size: 0.9em;
        }

        .text-muted {
          color: #6c757d !important;
        }

        a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default OrganizationSummaryTab;



